import { Button, Form, Input } from '@bancobarigui/barihaus';
import { Table, Space, Modal, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { StatusBadge } from '@/components/StatusBadge';
import { usePropertyTypeList } from './usePropertyTypeList';
import type { PropertyType } from '@/services/types/user.types';

export function PropertyTypeList() {
  const {
    propertyTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleNameChange,
    handleModalCancel,
    handleModalSubmit,
  } = usePropertyTypeList();

  const tableColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: PropertyType, b: PropertyType) => a.name.localeCompare(b.name),
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <StatusBadge
          label={isActive ? 'Ativo' : 'Inativo'}
          color={isActive ? '#22c55e' : '#ef4444'}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: PropertyType) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Excluir
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Tipos de Imóvel"
          actions={
            <Button buttonColor="primary" icon={<PlusOutlined />} onClick={handleNew}>
              Novo Tipo de Imóvel
            </Button>
          }
        />
      }
    >
      <FeatureAlert services={['user']} />
      <ServiceCheck services={['user']} featureName="Tipos de Imovel">
        <div className="flex flex-col gap-4">
        <Table
          columns={tableColumns}
          dataSource={propertyTypes}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total de ${total} tipos`,
          }}
          scroll={{ x: 1000 }}
        />

        <Modal
          title={editingItem ? 'Editar Tipo de Imóvel' : 'Novo Tipo de Imóvel'}
          open={isModalOpen}
          onCancel={handleModalCancel}
          onOk={handleModalSubmit}
          confirmLoading={submitting}
          okText={editingItem ? 'Salvar' : 'Criar'}
          cancelText="Cancelar"
        >
          <Form
            form={form}
            layout="vertical"
            className="mt-4"
          >
            <Form.Item
              name="name"
              label="Nome"
              rules={[{ required: true, message: 'Por favor, informe o nome' }]}
            >
              <Input
                placeholder="Nome do tipo de imóvel"
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="code"
              label="Código"
              rules={[{ required: true, message: 'Por favor, informe o código' }]}
            >
              <Input placeholder="Código do tipo de imóvel" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descrição"
            >
              <Input placeholder="Descrição (opcional)" />
            </Form.Item>

            {editingItem && (
              <Form.Item
                name="isActive"
                label="Ativo"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            )}
          </Form>
        </Modal>
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
